import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { billsService } from '../../../services/payables/bills';
import { vendorsService } from '../../../services/payables/vendors';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch, FaMoneyBillWave, FaFileInvoice, FaExclamationTriangle } from 'react-icons/fa';
import LoadingSpinner from '../../ui/LoadingSpinner';
import CreateBillModal from '../../bills/CreateBillModal';
import EditBillModal from '../../bills/EditBillModal';
import BillDetailModal from '../../bills/BillDetailModal';
import PayBillModal from '../../bills/PayBillModal';
import { formatDate, formatCurrency } from '../../../utils/formatting';

const BillsTab = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState({
    totalBills: 0,
    unpaidBills: 0,
    overdueBills: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalOutstanding: 0
  });

  useEffect(() => {
    fetchBills();
    fetchVendors();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBills = async () => {
    try {
      setLoading(true);
      setError(null);
      const billData = await billsService.getBills(user.companyId);
      setBills(billData);
      calculateSummary(billData);
    } catch (err) {
      console.error('Error fetching bills:', err);
      setError(t('bills.error.loading_failed', 'Failed to load bills'));
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const vendorData = await vendorsService.getVendors(user.companyId);
      setVendors(vendorData);
    } catch (err) {
      console.error('Error fetching vendors:', err);
    }
  };

  const calculateSummary = (billData) => {
    const now = new Date();
    const summary = billData.reduce((acc, bill) => {
      const dueDate = new Date(bill.dueDate);
      const isOverdue = dueDate < now && bill.paymentStatus !== 'paid';
      
      acc.totalBills += 1;
      acc.totalAmount += bill.totalAmount || 0;
      
      if (bill.paymentStatus === 'paid') {
        acc.totalPaid += bill.paidAmount || bill.totalAmount || 0;
      } else {
        acc.unpaidBills += 1;
        acc.totalOutstanding += (bill.totalAmount || 0) - (bill.paidAmount || 0);
        if (isOverdue) {
          acc.overdueBills += 1;
        }
      }
      
      return acc;
    }, {
      totalBills: 0,
      unpaidBills: 0,
      overdueBills: 0,
      totalAmount: 0,
      totalPaid: 0,
      totalOutstanding: 0
    });
    
    setSummary(summary);
  };

  const handleCreateBill = () => {
    setShowCreateModal(true);
  };

  const handleEditBill = (bill) => {
    setSelectedBill(bill);
    setShowEditModal(true);
  };

  const handleViewBill = (bill) => {
    setSelectedBill(bill);
    setShowDetailModal(true);
  };

  const handlePayBill = (bill) => {
    setSelectedBill(bill);
    setShowPayModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPayModal(false);
    fetchBills(); // Refresh bills to show updated payment status
    fetchVendors(); // Refresh vendors to show updated balances
  };

  const handleDeleteBill = async (billId) => {
    if (window.confirm(t('bills.confirm.delete_bill', 'Are you sure you want to delete this bill?'))) {
      try {
        await billsService.deleteBill(billId);
        fetchBills();
      } catch (err) {
        console.error('Error deleting bill:', err);
      }
    }
  };

  const filteredBills = bills.filter(bill => {
    const matchesSearch = bill.billNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bill.vendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bill.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || bill.status === statusFilter;
    const matchesVendor = vendorFilter === 'all' || bill.vendorId === vendorFilter;
    
    return matchesSearch && matchesStatus && matchesVendor;
  });

  const getStatusBadge = (bill) => {
    const now = new Date();
    const dueDate = new Date(bill.dueDate);
    const isOverdue = dueDate < now && bill.paymentStatus !== 'paid';
    
    let status = bill.paymentStatus || 'unpaid';
    if (isOverdue && status !== 'paid') {
      status = 'overdue';
    }
    
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', text: t('bills.status.draft', 'Draft') },
      unpaid: { color: 'bg-yellow-100 text-yellow-800', text: t('bills.status.pending', t('common.pending')) },
      partial: { color: 'bg-blue-100 text-blue-800', text: t('bills.status.partial', 'Partially Paid') },
      paid: { color: 'bg-green-100 text-green-800', text: t('bills.status.paid', t('common.paid')) },
      overdue: { color: 'bg-red-100 text-red-800', text: t('bills.status.overdue', t('common.overdue')) }
    };
    
    const config = statusConfig[status] || statusConfig.unpaid;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <FaExclamationTriangle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{error}</h3>
        <div className="mt-6">
          <button
            onClick={fetchBills}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            {t('common.try_again', 'Try Again')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('bills.title')}</h2>
          <p className="text-gray-600">{t('bills.subtitle')}</p>
        </div>
        <button
          onClick={handleCreateBill}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <FaPlus className="mr-2 h-4 w-4" />
          {t('bills.create_bill', 'Create Bill')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FaFileInvoice className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  {t('bills.total_bills', 'Total Bills')}
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {summary.totalBills}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FaMoneyBillWave className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  {t('bills.total_paid', 'Total Paid')}
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {formatCurrency(summary.totalPaid)}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  {t('bills.unpaid_bills', 'Unpaid Bills')}
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {summary.unpaidBills}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  {t('bills.overdue_bills', 'Overdue Bills')}
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {summary.overdueBills}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.search', t('common.search'))}
            </label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('bills.search_placeholder', 'Search bills...')}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.status', t('common.status'))}
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('bills.filter.all_statuses', 'All Statuses')}</option>
              <option value="draft">{t('bills.status.draft', 'Draft')}</option>
              <option value="pending">{t('bills.status.pending', t('common.pending'))}</option>
              <option value="paid">{t('bills.status.paid', t('common.paid'))}</option>
              <option value="overdue">{t('bills.status.overdue', t('common.overdue'))}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('vendors.vendor', 'Vendor')}
            </label>
            <select
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('bills.filter.all_vendors', 'All Vendors')}</option>
              {vendors.map(vendor => (
                <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Bills Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('bills.bill_number', 'Bill Number')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('vendors.vendor', 'Vendor')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('bills.bill_date', 'Bill Date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('bills.due_date', 'Due Date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.amount', t('common.amount'))}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('bills.remaining_balance', 'Remaining Balance')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.status', t('common.status'))}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.actions', t('common.actions'))}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBills.map((bill) => (
                <tr key={bill.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {bill.billNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {bill.vendorName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(bill.billDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(bill.dueDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(bill.totalAmount || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`font-medium ${(bill.remainingAmount || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(bill.remainingAmount || 0)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(bill)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewBill(bill)}
                        className="text-blue-600 hover:text-blue-900"
                        title={t('common.view', t('common.view'))}
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleEditBill(bill)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title={t('common.edit', t('common.edit'))}
                      >
                        <FaEdit />
                      </button>
                      {bill.paymentStatus !== 'paid' && bill.remainingAmount > 0 && (
                        <button
                          onClick={() => handlePayBill(bill)}
                          className="text-green-600 hover:text-green-900"
                          title={t('bills.pay_bill', 'Pay Bill')}
                        >
                          <FaMoneyBillWave />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteBill(bill.id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('common.delete', t('common.delete'))}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredBills.length === 0 && (
          <div className="text-center py-12">
            <FaFileInvoice className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('bills.no_bills', 'No bills found')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('bills.no_bills_desc', 'Get started by creating a new bill.')}
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreateBill}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <FaPlus className="mr-2 h-4 w-4" />
                {t('bills.create_bill', 'Create Bill')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateBillModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={async (billData) => {
            try {
              await billsService.addBill({
                ...billData,
                companyId: user.companyId,
                createdBy: user.uid
              });
              setShowCreateModal(false);
              fetchBills();
            } catch (err) {
              console.error('Error creating bill:', err);
            }
          }}
          vendors={vendors}
        />
      )}

      {showEditModal && selectedBill && (
        <EditBillModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchBills();
          }}
          bill={selectedBill}
          vendors={vendors}
        />
      )}

      {showDetailModal && selectedBill && (
        <BillDetailModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          bill={selectedBill}
        />
      )}

      {showPayModal && selectedBill && (
        <PayBillModal
          isOpen={showPayModal}
          onClose={() => setShowPayModal(false)}
          onSuccess={handlePaymentSuccess}
          bill={selectedBill}
        />
      )}
    </div>
  );
};

export default BillsTab;